import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CardBody, Button } from "reactstrap";
import api from "../api";
import jsPDF from "jspdf";
import nttlogo from "../assets/images/adaptis-dark.webp";
import axios from "axios";
import VendorLogo from "../components/VendorLogo";
import SecondaryLogo from "../components/SecondaryLogo";
import { CheckCircle, XCircle } from "lucide-react";
import { showError, showSuccess } from "../components/AppToast";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useAuth } from "../auth/AuthContext";

// ---------- PDF colors (kept as one place to tweak the theme) ----------
const COLORS = {
  navy: [13, 30, 66],
  green: [22, 163, 74],
  red: [220, 38, 38],
  gray: [100, 116, 139],
  darkText: [15, 23, 42],
  border: [226, 232, 240],
  sectionBg: [248, 250, 252],
};

// ---------- Shared helper: pull email/phone out of receipt_data ----------
const normalizeLabel = (l) =>
  (l || "").toString().toLowerCase().replace(/[^a-z]/g, "");

const extractContactInfo = (receiptData) => {
  const emailItem = receiptData.find((item) =>
    normalizeLabel(item?.label).includes("email"),
  );
  const phoneItem = receiptData.find((item) => {
    const n = normalizeLabel(item?.label);
    return (
      n.includes("phone") || n.includes("mobile") || n.includes("contactnumber")
    );
  });

  const filteredReceiptData = receiptData.filter(
    (item) => item !== emailItem && item !== phoneItem,
  );

  return {
    emailItem,
    phoneItem,
    filteredReceiptData,
    showCustomerSection: !!(emailItem || phoneItem),
  };
};

// ---------- Same initials logic as VendorLogo.jsx (kept in sync manually) ----------
const getInitials = (name) => {
  if (!name) return "NA";
  const words = name.trim().split(" ");
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  return (words[0][0] + words[1][0]).toUpperCase();
};

// ---------- Reusable detail row (plain flex, not Bootstrap Row/Col) ----------
// Avoids the flex "min-width:auto" stacking bug on narrow screens.
const DetailRow = ({ label, value, emphasizeValue = true, breakAll = false }) => (
  <div className="detail-row">
    <span className="detail-label">{label}</span>
    <span
      className={`detail-value ${emphasizeValue ? "detail-value--bold" : ""} ${breakAll ? "detail-value--break-all" : ""
        }`}
    >
      {value !== null && value !== undefined && value !== "" ? value : "N/A"}
    </span>
  </div>
);

export default function ModernPaymentReceipt() {
  const { transactionId } = useParams();
  const [txnData, setTxnData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [receiptSent, setReceiptSent] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // const {token,role} = useAuth();

  const navigate = useNavigate();

  const encoded = localStorage.getItem("role");
  const role = encoded ? atob(encoded) : null;

  const fetchTxn = async () => {
    try {
      const response = await api.post(
        `${process.env.REACT_APP_BASE_URL_DEV}/payment/fetchByTransactionId`,
        { merchantTransactionId: transactionId },
        { headers: { "Content-Type": "application/json" } },
      );

      const data = response.data;

      const normalized = {
        ...data,
        transactionStatusCode:
          data.transactionStatusCode ||
          data.statusCode ||
          data?.transactionStatus ||
          null,
      };

      setTxnData(normalized);
    } catch (err) {
      navigate("/error/network");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTxn();
  }, [transactionId]);

  // ---------- Load a logo/image as a base64 PNG for jsPDF (downscaled to keep file size small) ----------
  const loadImageAsDataURL = (src, maxWidth = 320) =>
    new Promise((resolve) => {
      if (!src) return resolve(null);
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const scale = Math.min(1, maxWidth / img.naturalWidth);
          const w = Math.max(1, Math.round(img.naturalWidth * scale));
          const h = Math.max(1, Math.round(img.naturalHeight * scale));

          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, w, h);
          resolve({
            dataUrl: canvas.toDataURL("image/png"),
            ratio: h / w,
          });
        } catch (e) {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = src;
    });

  // ---------- Draw a rounded-square initials avatar as a fallback (mirrors VendorLogo.jsx's avatar fallback) ----------
  const buildInitialsAvatarDataURL = (name, size = 160) => {
    try {
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");

      // Rounded-square background
      const radius = size * 0.2;
      ctx.fillStyle = "rgb(13, 30, 66)"; // navy, matches COLORS.navy
      ctx.beginPath();
      ctx.moveTo(radius, 0);
      ctx.lineTo(size - radius, 0);
      ctx.quadraticCurveTo(size, 0, size, radius);
      ctx.lineTo(size, size - radius);
      ctx.quadraticCurveTo(size, size, size - radius, size);
      ctx.lineTo(radius, size);
      ctx.quadraticCurveTo(0, size, 0, size - radius);
      ctx.lineTo(0, radius);
      ctx.quadraticCurveTo(0, 0, radius, 0);
      ctx.closePath();
      ctx.fill();

      // Initials text
      ctx.fillStyle = "#ffffff";
      ctx.font = `${Math.round(size * 0.38)}px Helvetica, Arial, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(getInitials(name), size / 2, size / 2 + 2);

      return { dataUrl: canvas.toDataURL("image/png"), ratio: 1, isFallback: true };
    } catch (e) {
      return null;
    }
  };

  // ---------- Resolve the vendor/slug logo the same way VendorLogo.jsx does, with the same fallback ----------
  const loadVendorLogo = async (slug) => {
    if (!slug) return null;
    const BASE_URL = process.env.REACT_APP_BASE_URL_DEV;
    const vendorLogoUrl = `${BASE_URL}/forms/slug/${slug}/logo`;

    const loaded = await loadImageAsDataURL(vendorLogoUrl, 320);
    if (loaded) return loaded;

    // Falls back to initials avatar, just like the on-screen VendorLogo component
    return buildInitialsAvatarDataURL(slug, 160);
  };
  const loadSecondaryLogo = async (slug) => {
  if (!slug) return null;

  const BASE_URL = process.env.REACT_APP_BASE_URL_DEV;
  const secondaryLogoUrl =
    `${BASE_URL}/forms/slug/${slug}/secondary-logo`;

  return await loadImageAsDataURL(secondaryLogoUrl, 320);
};

  // ---------- Build a crisp, vector-based, size-optimized PDF ----------
  const buildReceiptPdf = async (data) => {
    let receiptData = [];
    try {
      receiptData = JSON.parse(data?.receipt_data || "[]");
      if (typeof receiptData === "string") receiptData = JSON.parse(receiptData);
    } catch (e) {
      receiptData = [];
    }

    // const { emailItem, phoneItem, filteredReceiptData, showCustomerSection } =
    //   extractContactInfo(receiptData);
    const filteredReceiptData = receiptData;

    const pdf = new jsPDF({
      orientation: "p",
      unit: "mm",
      format: "a4",
      compress: true,
    });

    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 16;
    const contentWidth = pageWidth - margin * 2;
    let y = margin;

    const checkPageBreak = (needed) => {
      if (y + needed > pageHeight - 20) {
        pdf.addPage();
        y = margin;
      }
    };

    // Header: vendor/slug logo (left) + NTT/Adaptis logo (right), vertically centered on each other
    const headerBoxH = 22; // shared bounding box height both logos are centered within
    const vendorLogo = await loadVendorLogo(data.slug);
    const secondaryLogo = await loadSecondaryLogo(data.slug);
    const nttLogoImg = await loadImageAsDataURL(nttlogo, 640);

  let logoX = margin;

if (vendorLogo) {
  const boxH = vendorLogo.isFallback ? headerBoxH * 0.7 : headerBoxH;
  const drawH = Math.min(boxH, boxH * vendorLogo.ratio);
  const drawW = drawH / vendorLogo.ratio;
  const drawY = y + (headerBoxH - drawH) / 2;

  pdf.addImage(
    vendorLogo.dataUrl,
    "PNG",
    logoX,
    drawY,
    drawW,
    drawH,
    undefined,
    "FAST"
  );

  logoX += drawW + 4; // spacing between logos
}

if (secondaryLogo) {
  const drawH = Math.min(headerBoxH, headerBoxH * secondaryLogo.ratio);
  const drawW = drawH / secondaryLogo.ratio;
  const drawY = y + (headerBoxH - drawH) / 2;

  pdf.addImage(
    secondaryLogo.dataUrl,
    "PNG",
    logoX,
    drawY,
    drawW,
    drawH,
    undefined,
    "FAST"
  );
}

    if (nttLogoImg) {
      // Adaptis wordmark is wide and short, so cap by width instead of height
      // so it renders at a legible, properly proportioned size (not tiny).
      const maxW = 42;
      const maxH = headerBoxH;
      let drawW = maxW;
      let drawH = drawW * nttLogoImg.ratio;
      if (drawH > maxH) {
        drawH = maxH;
        drawW = drawH / nttLogoImg.ratio;
      }
      const drawY = y + (headerBoxH - drawH) / 2;
      pdf.addImage(
        nttLogoImg.dataUrl,
        "PNG",
        pageWidth - margin - drawW,
        drawY,
        drawW,
        drawH,
        undefined,
        "FAST"
      );
    }
    y += headerBoxH + 2;

    pdf.setDrawColor(...COLORS.border);
    pdf.line(margin, y, pageWidth - margin, y);
    y += 10;

    // Form Title
    if (data.title) {
      pdf.setFontSize(16);
      pdf.setFont(undefined, "bold");
      pdf.setTextColor(...COLORS.darkText);
      pdf.text(String(data.title), pageWidth / 2, y, { align: "center" });
      y += 9;
    }

    // Status
    pdf.setFontSize(13);
    pdf.setFont(undefined, "bold");
    pdf.setTextColor(...COLORS.green);
    pdf.text("Payment Successful", pageWidth / 2, y, { align: "center" });
    y += 12;

    // Amount
    pdf.setFontSize(24);
    pdf.setTextColor(...COLORS.darkText);
    pdf.text(
      `Rs. ${Number(data.totalAmount || 0).toLocaleString("en-IN")}`,
      pageWidth / 2,
      y,
      { align: "center" }
    );
    y += 10;

    pdf.setDrawColor(...COLORS.border);
    pdf.line(margin, y, pageWidth - margin, y);
    y += 4;

    // ---- helpers for label/value blocks ----
    const sectionTitle = (title) => {
      checkPageBreak(12);
      pdf.setFillColor(...COLORS.sectionBg);
      pdf.rect(margin, y, contentWidth, 8, "F");
      pdf.setFontSize(10);
      pdf.setFont(undefined, "bold");
      pdf.setTextColor(...COLORS.darkText);
      pdf.text(title, margin + 3, y + 5.5);
      y += 8;
    };

    // const row = (label, value) => {
    //   pdf.setFontSize(10);
    //   const valueText = pdf.splitTextToSize(String(value ?? "N/A"), contentWidth * 0.55);
    //   const rowHeight = Math.max(10, valueText.length * 5 + 4);
    //   checkPageBreak(rowHeight);

    //   pdf.setFont(undefined, "normal");
    //   pdf.setTextColor(...COLORS.gray);
    //   pdf.text(String(label), margin + 3, y + 6);

    //   pdf.setFont(undefined, "bold");
    //   pdf.setTextColor(...COLORS.darkText);
    //   pdf.text(valueText, pageWidth - margin - 3, y + 6, { align: "right" });

    //   y += rowHeight;
    //   pdf.setDrawColor(...COLORS.border);
    //   pdf.line(margin, y, pageWidth - margin, y);
    // };
    const row = (label, value) => {
      const displayValue =
        value === null ||
          value === undefined ||
          value === "" ||
          value === "null" ||
          value === "undefined"
          ? "N/A"
          : value;

      pdf.setFontSize(10);

      const valueText = pdf.splitTextToSize(
        String(displayValue),
        contentWidth * 0.55
      );

      const rowHeight = Math.max(10, valueText.length * 5 + 4);

      checkPageBreak(rowHeight);

      pdf.setFont(undefined, "normal");
      pdf.setTextColor(...COLORS.gray);
      pdf.text(String(label), margin + 3, y + 6);

      pdf.setFont(undefined, "bold");
      pdf.setTextColor(...COLORS.darkText);
      pdf.text(valueText, pageWidth - margin - 3, y + 6, {
        align: "right",
      });

      y += rowHeight;

      pdf.setDrawColor(...COLORS.border);
      pdf.line(margin, y, pageWidth - margin, y);
    };

    sectionTitle("Transaction Summary");
    row("Transaction ID", data.atomTxnId);
    row("Date & Time", data.createdAt ? new Date(data.createdAt).toLocaleString() : "N/A");
    row("Payment Method", data.payMode);
    y += 8;

    if (filteredReceiptData.length > 0) {
      sectionTitle("Receipt Details");
      filteredReceiptData.forEach((item) => row(item?.label || "N/A", item?.value));
      y += 8;
    }

    // if (showCustomerSection) {
    //   sectionTitle("Customer Information");
    //   if (emailItem) row("Email", emailItem.value);
    //   if (phoneItem) row("Mobile", phoneItem.value);
    // }

    const pageCount = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setTextColor(...COLORS.gray);
      pdf.text("Powered by NTT DATA Payment Services", pageWidth / 2, pageHeight - 10, {
        align: "center",
      });
    }

    return pdf;
  };

  useEffect(() => {
    const sendReceipt = async () => {
      const atomId = sessionStorage.getItem("atomTokenId");
      if (!txnData || !atomId || receiptSent) return;
      setReceiptSent(true);

      try {
        const email = txnData.customerEmail;
        const customerName = txnData.customer_name || "Customer";
        const txnId = txnData?.merchantTransactionId;

        const pdf = await buildReceiptPdf(txnData);
        const pdfBlob = pdf.output("blob");

        const file = new File([pdfBlob], `Receipt_${txnId}.pdf`, {
          type: "application/pdf",
        });

        const payload = new FormData();
        payload.append("email", email);
        payload.append("transaction_id", txnId);
        payload.append("customer_name", customerName);
        payload.append("file", file);

        await api.post(
          `${process.env.REACT_APP_BASE_URL_DEV}/receipt/send`,
          payload,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
      } catch (err) {
        console.error("Error sending receipt:", err);
      }
    };

    sendReceipt();
  }, [txnData, receiptSent]);

  const handleDownload = async () => {
    if (!txnData) return;
    try {
      setDownloading(true);
      const pdf = await buildReceiptPdf(txnData);
      pdf.save(`Receipt_${txnData?.atomTxnId || "download"}.pdf`);
    } catch (e) {
      showError("Failed to generate receipt");
    } finally {
      setDownloading(false);
    }
  };

  function parseFormResponse(apiData) {
    const i = apiData?.payInstrument?.[0];
    if (!i) return null;

    return {
      atomTxnId: i.payDetails?.atomTxnId,
      totalAmount: i.payDetails?.totalAmount,
      amount: i.payDetails?.amount,
      statusCode: i.responseDetails?.statusCode,
      message: i.responseDetails?.message,
      payMode: i.payModeSpecificData?.subChannel,
    };
  }

  const handleRefreshStatus = async (merchantTransactionId) => {
    try {
      setRefreshing(true);

      const token = localStorage.getItem("jwt");

      const response = await api.post(
        "/forms/transactionStatus",
        { merchantTransactionId },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const parsed = parseFormResponse(response.data);
      if (!parsed) return showError("Invalid response");

      const amountValue = parsed.totalAmount ?? parsed.amount;

      const hasValidTotalAmount =
        amountValue != null && amountValue !== "" && Number(amountValue) > 0;

      const isNowValid =
        hasValidTotalAmount && !!parsed.atomTxnId && parsed.statusCode === "OTS0000";

      if (isNowValid) {
        showSuccess("Payment successful");
        setTxnData(null);
        setLoading(true);
        await fetchTxn();
      } else {
        setTxnData((prev) => ({
          ...prev,
          ...parsed,
          transactionStatusCode: parsed.statusCode,
        }));
        showError(parsed.message || "Pending");
      }
    } catch {
      showError("Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="receipt-page">
        <div className="receipt-shell pt-4 pb-1 px-4">
          <CardBody>
            {/* ---------- Header card skeleton ---------- */}
            <div className="receipt-header-card">
              <div className="header-top-row">
                <div className="receipt-vendor">
                  <Skeleton width={56} height={56} borderRadius={14} />
                </div>

                <div className="header-title-inline">
                  <Skeleton width="70%" height={20} />
                </div>

                <Skeleton circle width={22} height={22} />
              </div>

              <div className="header-status-centered">
                <Skeleton width={140} height={16} style={{ margin: "6px auto 0" }} />
              </div>

              <div className="header-amount-centered">
                <Skeleton width={150} height={32} style={{ margin: "4px auto 0" }} />
              </div>

              <div className="summary-grid-compact">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="summary-item-compact">
                    <Skeleton width={90} height={12} />
                    <Skeleton width={110} height={14} />
                  </div>
                ))}
              </div>
            </div>

            {/* ---------- Receipt Details skeleton ---------- */}
            <div className="receipt-section">
              <div className="section-title">
                <Skeleton width={130} height={16} />
              </div>
              <div className="section-body">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="detail-row">
                    <span className="detail-label">
                      <Skeleton width={90} height={14} />
                    </span>
                    <span className="detail-value">
                      <Skeleton width={110} height={14} />
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* ---------- Customer Information skeleton ---------- */}
            <div className="receipt-section">
              <div className="section-title">
                <Skeleton width={160} height={16} />
              </div>
              <div className="section-body">
                <div className="detail-row">
                  <span className="detail-label">
                    <Skeleton width={60} height={14} />
                  </span>
                  <span className="detail-value">
                    <Skeleton width={150} height={14} />
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">
                    <Skeleton width={70} height={14} />
                  </span>
                  <span className="detail-value">
                    <Skeleton width={100} height={14} />
                  </span>
                </div>
              </div>
            </div>

            <div className="no-print mt-4 mb-2">
              <Skeleton width="100%" height={50} borderRadius={10} />
            </div>
          </CardBody>

          <div className="receipt-powered text-center">
            <Skeleton width={180} height={16} style={{ display: "block", margin: "0 auto" }} />
          </div>
        </div>
      </div>
    );
  } if (!txnData) return <div>No transaction found</div>;

  if (role !== "MERCHANT" && !sessionStorage.getItem("atomTokenId")) {
    return <div>Transaction not found</div>;
  }

  const hasAtomTxnId =
    txnData.atomTxnId != null &&
    txnData.atomTxnId !== "" &&
    txnData.atomTxnId !== "null" &&
    txnData.atomTxnId !== "undefined";

  const hasPayMode =
    txnData.payMode != null &&
    txnData.payMode !== "" &&
    txnData.payMode !== "null" &&
    txnData.payMode !== "undefined";

  const isValid = txnData.totalAmount > 0 && hasAtomTxnId && hasPayMode;

  let receiptData = [];
  try {
    receiptData = JSON.parse(txnData?.receipt_data || "[]");
   
    if (typeof receiptData === "string") receiptData = JSON.parse(receiptData);
  } catch (e) {
    receiptData = [];
  }

  // const { emailItem, phoneItem, filteredReceiptData, showCustomerSection } =
  //   extractContactInfo(receiptData);
  const filteredReceiptData = receiptData;

  return (
    <div className="receipt-page">
      <div className="receipt-shell pt-4 pb-1 px-4" id="receipt-capture">
        <CardBody>
          {!isValid ? (
            <div className="text-center">
              <XCircle size={52} color="red" />
              <h4 className="mt-3">There was some error in your transaction</h4>
              <p>Please refresh the transaction status to view the latest status.</p>

              <div className="mt-3 no-print">
                <Button
                  className="download-btn w-100"
                  onClick={() => handleRefreshStatus(txnData?.merchantTransactionId)}
                  disabled={refreshing}
                >
                  {refreshing ? "Refreshing..." : "Refresh Status"}
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* ---------- Header card: logo (left) / title (true center) / tick (right) ---------- */}
              <div className="receipt-header-card">
                <div className="header-top-row">
                  <div className="receipt-vendor">
                    <VendorLogo slug={txnData.slug} />
                    <SecondaryLogo slug={txnData.slug} />
                  </div>

                  {txnData.title && (
                    <div className="header-title-inline">
                      <span>{txnData.title}</span>
                    </div>
                  )}

                  <CheckCircle
                    className="header-check-icon"
                    size={22}
                    strokeWidth={2.5}
                    color="#16a34a"
                  />
                </div>

                <div className="header-status-centered">
                  <span>Payment Successful</span>
                </div>

                <div className="header-amount-centered">
                  <span className="summary-value amount">
                    ₹ {Number(txnData.totalAmount || 0).toLocaleString()}
                  </span>
                </div>

                <div className="summary-grid-compact">
                  <div className="summary-item-compact">
                    <span className="summary-label">Transaction ID</span>
                    <span className="summary-value">{txnData.atomTxnId || "N/A"}</span>
                  </div>

                  <div className="summary-item-compact">
                    <span className="summary-label">Date & Time</span>
                    <span className="summary-value">
                      {txnData.createdAt ? new Date(txnData.createdAt).toLocaleString() : "N/A"}
                    </span>
                  </div>

                  <div className="summary-item-compact">
                    <span className="summary-label">Payment Method</span>
                    <span className="summary-value">{txnData.payMode || "N/A"}</span>
                  </div>
                </div>
              </div>

              {/* Receipt Fields (email/phone stripped out if shown dedicatedly below) */}
              {filteredReceiptData.length > 0 && (
                <div className="receipt-section">
                  <div className="section-title">Receipt Details</div>
                  <div className="section-body">
                    {filteredReceiptData.map((item, index) => (
                      <DetailRow
                        key={index}
                        label={item?.label || "N/A"}
                        value={
                          item?.value === null ||
                            item?.value === undefined ||
                            item?.value === "" ||
                            item?.value === "null" ||
                            item?.value === "undefined"
                            ? "N/A"
                            : Array.isArray(item?.value)
                            ? item.value.join(", ")
                            : item?.value
                        }
                      />
                      // <DetailRow key={index} label={item?.label || "N/A"} value={item?.value} />
                    ))}
                  </div>
                </div>
              )}

              {/* Customer Details — only shown if receipt_data actually contained email/phone */}
              {/* {showCustomerSection && (
                <div className="receipt-section">
                  <div className="section-title">Customer Information</div>
                  <div className="section-body">
                    {emailItem && (
                      <DetailRow
                        label="Email"
                        value={
                          emailItem?.value === null ||
                            emailItem?.value === undefined ||
                            emailItem?.value === "" ||
                            emailItem?.value === "null" ||
                            emailItem?.value === "undefined"
                            ? "N/A"
                            : emailItem.value
                        }
                        breakAll
                      />
                      // <DetailRow label="Email" value={emailItem.value} breakAll />
                    )}
                    <DetailRow
                      label="Mobile"
                      value={
                        phoneItem?.value === null ||
                          phoneItem?.value === undefined ||
                          phoneItem?.value === "" ||
                          phoneItem?.value === "null" ||
                          phoneItem?.value === "undefined"
                          ? "N/A"
                          : phoneItem.value
                      }
                    />
                   {phoneItem && <DetailRow label="Mobile" value={phoneItem.value} />} 
                  </div>
                </div>
              )} */}

              <div className="mt-4 no-print">
                <Button
                  className="download-btn w-100"
                  onClick={handleDownload}
                  disabled={downloading}
                >
                  {downloading ? "Preparing PDF..." : "Download Receipt"}
                </Button>
              </div>
            </>
          )}
        </CardBody>

        <div className="d-flex flex-column">
          <div className="receipt-footer mt-auto text-center">
            <div className="receipt-powered mt-3 text-center">
              Powered by <img src={nttlogo} alt="Company" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
